import React from 'react'
import { useTranslation } from 'react-i18next'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import { Typogaphy } from 'vnetwork-ui'
import { FileText, CheckCircle } from 'lucide-react'

interface UploadedFile {
    file_name: string
    file_size: number
    file_type: string
}

interface ReviewFilesCardProps {
    files: UploadedFile[]
}

const formatFileSize = (bytes: number) => {
    if (bytes < 1024) return bytes + ' B'
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(2) + ' KB'
    return (bytes / (1024 * 1024)).toFixed(2) + ' MB'
}

const ReviewFilesCard: React.FC<ReviewFilesCardProps> = ({ files }) => {
    const { t } = useTranslation('asset')

    return (
        <Card className="border-gray-100 shadow-sm overflow-hidden">
            <CardHeader className="border-b border-gray-50 pb-3">
                <CardTitle className="flex items-center gap-2">
                    <Typogaphy className="font-semibold text-primary">
                        {t('form.uploadedFiles')} ({files.length})
                    </Typogaphy>
                </CardTitle>
            </CardHeader>
            <CardContent className="p-5">
                {files.length > 0 ? (
                    <div className="grid grid-cols-1 gap-3">
                        {files.map((file, index) => (
                            <div
                                key={index}
                                className="flex items-center justify-between p-3 bg-gray-50/50 rounded-xl border border-gray-100 group hover:border-amber-200 transition-colors"
                            >
                                <div className="flex items-center space-x-3 min-w-0 flex-1">
                                    <div className="p-2 bg-white rounded-lg shadow-sm">
                                        <FileText className="h-5 w-5 text-amber-600 flex-shrink-0" />
                                    </div>
                                    <div className="min-w-0 flex-1">
                                        <p className="text-sm font-medium text-gray-900 truncate">
                                            {file.file_name}
                                        </p>
                                        <p className="text-xs text-gray-500">
                                            {formatFileSize(file.file_size)} •{' '}
                                            {file.file_type}
                                        </p>
                                    </div>
                                </div>
                                <Badge
                                    variant="success"
                                    className="flex items-center gap-1 text-xs"
                                >
                                    <CheckCircle className="h-3 w-3" />
                                    Ready
                                </Badge>
                            </div>
                        ))}
                    </div>
                ) : (
                    <div className="text-center py-6 border border-dashed border-gray-200 rounded-xl">
                        <p className="text-sm text-gray-400">
                            {t('form.noFilesUploaded')}
                        </p>
                    </div>
                )}
            </CardContent>
        </Card>
    )
}

export default ReviewFilesCard
